import React from 'react';
import { Box, Typography, Divider, useTheme } from '@mui/material';
import { tokens } from '../../../../../theme';
import QuestionRender from '../../../../../components/QuestionRender';
import useSignedContent from '../../../../../utils/useSignedContent';

/**
 * Renders a single question's visual content (stem, stimulus, images, options)
 * for the left pane in Question UI mode. Read-only display — no answer inputs.
 */
const QuestionDisplay = ({ question, group, groupLabel }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // Sign S3 URLs in content
    const [signedPrompt, signedStimulus] = useSignedContent([
        question?.question_prompt || '',
        group?.stimulus_text || '',
    ]);

    if (!question) return null;

    return (
        <Box
            sx={{
                userSelect: 'none', WebkitUserSelect: 'none',
                '& img': { maxWidth: 500, height: 'auto' },
            }}
            onContextMenu={(e) => e.preventDefault()}
            onDragStart={(e) => e.preventDefault()}
        >
            {/* Question number header */}
            <Typography variant="h6" fontWeight={600} mb={1.5} color={colors.blueAccent[400]}>
                Question {question.question_number}
            </Typography>

            {/* Group stimulus (shared passage/context) */}
            {group && (group.stimulus_text || group.stimulus_url) && (
                <Box sx={{
                    mb: 2.5,
                    pb: 2,
                    borderBottom: `1px solid ${colors.grey[700]}`,
                }}>
                    {groupLabel && (
                        <Typography variant="caption" sx={{
                            display: 'inline-block', mb: 1, px: 1, py: 0.25, borderRadius: 1,
                            fontWeight: 700, letterSpacing: '.04em',
                            color: colors.blueAccent[300], bgcolor: colors.primary[600],
                            border: `1px solid ${colors.blueAccent[700]}`,
                        }}>
                            {groupLabel}
                        </Typography>
                    )}
                    {group.stimulus_text && (
                        <QuestionRender
                            content={signedStimulus}
                            sx={{ fontSize: '0.95rem', lineHeight: 1.7, mb: 1.5 }}
                        />
                    )}
                    {group.stimulus_url && (
                        <Box sx={{ my: 1.5, textAlign: 'center' }}>
                            <img
                                src={group.stimulus_url}
                                alt=""
                                draggable={false}
                                style={{ maxWidth: 500, width: '100%', height: 'auto' }}
                            />
                        </Box>
                    )}
                </Box>
            )}

            {/* Question prompt */}
            {question.question_prompt && (
                <QuestionRender
                    content={signedPrompt}
                    sx={{ fontSize: '1rem', lineHeight: 1.7, mb: 1.5 }}
                />
            )}

            {/* MCQ options display (read-only, for reference) */}
            {question.v2_question_options?.length > 0 && (
                <Box sx={{ mt: 2 }}>
                    {question.v2_question_options
                        .sort((a, b) => (a.order || 0) - (b.order || 0))
                        .map((opt) => (
                            <Box key={opt.id || opt.option_label} sx={{ display: 'flex', gap: 1.5, mb: 1.2, alignItems: 'flex-start' }}>
                                <Typography
                                    variant="body1"
                                    fontWeight={600}
                                    sx={{ minWidth: 24, color: colors.grey[300] }}
                                >
                                    {opt.option_label}.
                                </Typography>
                                {opt.option_text ? (
                                    <QuestionRender
                                        content={opt.option_text}
                                        sx={{ fontSize: '0.95rem', lineHeight: 1.6 }}
                                    />
                                ) : (
                                    <Typography variant="body1" color="text.secondary">
                                        {opt.option_label}
                                    </Typography>
                                )}
                            </Box>
                        ))
                    }
                </Box>
            )}

            {/* FRQ parts display (read-only prompts) */}
            {question.question_type !== 'mcq' && question.v2_question_parts?.length > 0 && (
                <Box sx={{ mt: 2 }}>
                    <Divider sx={{ mb: 1.5 }} />
                    {renderParts(question.v2_question_parts, colors)}
                </Box>
            )}
        </Box>
    );
};

/** Recursively render FRQ part prompts */
const renderParts = (parts, colors, depth = 0) => {
    if (!parts || parts.length === 0) return null;
    return parts.map((part) => (
        <Box key={part.id || part.part_label} sx={{ ml: depth * 2, mb: 1 }}>
            <Typography variant="body2" fontWeight={600} color={colors.grey[300]}>
                ({part.part_label}) {part.max_points ? `[${part.max_points} pt${part.max_points > 1 ? 's' : ''}]` : ''}
            </Typography>
            {part.subparts && renderParts(part.subparts, colors, depth + 1)}
        </Box>
    ));
};

export default QuestionDisplay;
